import { Sort } from '@angular/material/sort';
import {
  PagedListRequestInterface,
  PagedResponseInterface,
  PaginatedAPIInterface,
} from '@vendasta/galaxy/table';
import { Observable, of } from 'rxjs';

/** A row's comparable value for a column, used for in-memory sorting. */
export type SortValue = string | number | Date | undefined;

/**
 * In-memory {@link PaginatedAPIInterface} over a fixed array — search, sort, and cursor paging are
 * all done client-side. It backs a `GalaxyDataSource` for canned / storybook data (and any caller that
 * already holds the full set). Real backend-paginated views supply their own API instead, so the table
 * components stay agnostic to where the data comes from.
 */
export class StaticTableApi<T> implements PaginatedAPIInterface<T, unknown, unknown> {
  constructor(
    /**
     * The backing rows, either a fixed array or a getter read live on each `get()`. Pass a getter when
     * the data arrives/changes async (e.g. a signal): keep one `GalaxyDataSource` instance and trigger a
     * refetch, instead of recreating the data source — the table only binds its data source once.
     */
    private readonly rowsSource: T[] | (() => T[]),
    /** Free-text haystack for a row, matched case-insensitively against the search term. */
    private readonly searchText: (row: T) => string,
    /** Comparable value for a row under the given column id; omit to disable sorting. */
    private readonly sortValue?: (row: T, columnId: string) => SortValue,
  ) {}

  private currentRows(): T[] {
    return typeof this.rowsSource === 'function' ? this.rowsSource() : this.rowsSource;
  }

  get(req: PagedListRequestInterface<unknown, unknown>): Observable<PagedResponseInterface<T>> {
    const allRows = this.currentRows();
    const term = (req.searchOptions?.text ?? '').trim().toLowerCase();
    let rows = term ? allRows.filter((r) => this.searchText(r).toLowerCase().includes(term)) : [...allRows];

    const sort = req.sorting?.[0];
    if (isSort(sort) && sort.direction && this.sortValue) {
      const dir = sort.direction === 'asc' ? 1 : -1;
      const valueOf = this.sortValue;
      rows = rows.sort((a, b) => compareValues(valueOf(a, sort.active), valueOf(b, sort.active)) * dir);
    }

    const pageSize = req.pagingOptions?.pageSize || rows.length || 1;
    const start = Number(req.pagingOptions?.cursor ?? '0') || 0;
    const nextStart = start + pageSize;
    const hasMore = nextStart < rows.length;

    return of({
      data: rows.slice(start, nextStart),
      pagingMetadata: {
        nextCursor: hasMore ? String(nextStart) : '',
        hasMore,
        totalResults: rows.length,
      },
    });
  }
}

/** Narrows the container's loosely-typed sort payload to a MatSort `Sort`. */
function isSort(value: unknown): value is Sort {
  return !!value && typeof value === 'object' && 'active' in value && 'direction' in value;
}

function compareValues(a: SortValue, b: SortValue): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}
